import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  TouchableOpacity, 
  StyleSheet, 
  ScrollView,
  ActivityIndicator,
  Modal
} from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import { auth } from '../firebaseConfig';
import {
  getTemplates,
  addTemplate,
  updateTemplate,
  deleteTemplate,
  getProjectExamples,
  addProjectExample,
  updateProjectExample,
  deleteProjectExample,
  seedHardcodedExamples,
  getUserPreferences,
  updateUserPreferences,
  getContractTemplates,
  addContractTemplate,
  updateContractTemplate,
  deleteContractTemplate,
  seedContractTemplate
} from '../services/firestoreService';
import { getUserScopeFilter } from '../services/profileService';
import { buildProjectExample, scanWebsiteForDescription } from '../services/openaiService';
import { HARDCODED_EXAMPLES } from '../hardcodedExamples';
import { DEFAULT_CONTRACT_TEMPLATE } from '../hardcodedContract';
import UserMenu from '../components/UserMenu';
import LandingStyleHeader from '../components/LandingStyleHeader';

export default function SettingsScreen({ navigation }) {
  const { colors, glass, isDark, gradients } = useTheme();
  const [activeTab, setActiveTab] = useState('templates'); // 'templates', 'examples', 'contracts', or 'preferences'
  const [templates, setTemplates] = useState([]);
  const [examples, setExamples] = useState([]);
  const [contractTemplates, setContractTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [aiBuilderMode, setAiBuilderMode] = useState(false);
  const [aiBuilderData, setAiBuilderData] = useState({
    description: '',
    websiteUrl: '',
    caseStudyUrl: '',
    additionalContext: ''
  });
  const [buildingExample, setBuildingExample] = useState(false);
  const [scanningWebsite, setScanningWebsite] = useState(false);
  const [strictAdherenceDefault, setStrictAdherenceDefault] = useState(false);
  const [scopeFilter, setScopeFilter] = useState('all'); // all, organization, personal

  useEffect(() => {
    loadData();
    document.title = 'Flomodo - Settings';
  }, []);

  const loadData = async () => {
    try {
      const [templatesData, examplesData, contractTemplatesData, preferencesData, userScopeFilter] = await Promise.all([
        getTemplates(),
        getProjectExamples(),
        getContractTemplates(),
        getUserPreferences(),
        getUserScopeFilter(auth.currentUser.uid)
      ]);
      
      // Set scope filter
      setScopeFilter(userScopeFilter);
      
      // Apply scope filter to templates
      let filteredTemplates = templatesData;
      if (userScopeFilter !== 'all') {
        filteredTemplates = templatesData.filter(t => t.scope === userScopeFilter);
      }
      
      // Apply scope filter to examples
      let filteredExamples = examplesData;
      if (userScopeFilter !== 'all') {
        filteredExamples = examplesData.filter(e => e.scope === userScopeFilter);
      }
      
      // Apply scope filter to contract templates
      let filteredContractTemplates = contractTemplatesData;
      if (userScopeFilter !== 'all') {
        filteredContractTemplates = contractTemplatesData.filter(c => c.scope === userScopeFilter);
      }
      
      console.log('⚙️ Settings data loaded with filter:', {
        templates: filteredTemplates.length,
        examples: filteredExamples.length,
        contracts: filteredContractTemplates.length,
        filter: userScopeFilter
      });
      
      setTemplates(filteredTemplates);
      setExamples(filteredExamples);
      setContractTemplates(filteredContractTemplates);
      setStrictAdherenceDefault(preferencesData.strictAdherenceDefault || false);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStrictAdherence = async () => {
    const newValue = !strictAdherenceDefault;
    setStrictAdherenceDefault(newValue);
    try {
      await updateUserPreferences({ strictAdherenceDefault: newValue });
    } catch (error) {
      console.error('Error updating preferences:', error);
      alert('Error saving preference: ' + error.message);
      setStrictAdherenceDefault(!newValue); // Revert on error
    }
  };

  const handleSeedExamples = async (forceReseed = false) => {
    const message = forceReseed 
      ? 'This will DELETE all existing examples and re-import 18 examples with updated data (technologies & case studies). Continue?' 
      : 'This will add 18 pre-loaded project examples to your account. Continue?';
    
    if (confirm(message)) {
      setLoading(true);
      try {
        await seedHardcodedExamples(HARDCODED_EXAMPLES, forceReseed);
        await loadData();
        const action = forceReseed ? 'Reseeded' : 'Successfully added';
        alert(`${action} 18 project examples with technologies and case studies!`);
      } catch (error) {
        alert('Error seeding examples: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Template functions
  const openTemplateModal = (templateOrType = null) => {
    // Check if it's a template object or a string type
    if (templateOrType && typeof templateOrType === 'object') {
      // It's an existing template
      setCurrentItem(templateOrType);
      setFormData({
        name: templateOrType.name,
        content: templateOrType.content,
        templateType: templateOrType.templateType || 'proposal',
        strictAdherence: templateOrType.strictAdherence || false
      });
    } else {
      // It's a new template with optional type
      setCurrentItem(null);
      const templateType = templateOrType && typeof templateOrType === 'string' ? templateOrType : 'proposal';
      setFormData({ name: '', content: '', templateType, strictAdherence: false });
    }
    setModalVisible(true);
  };

  const saveTemplate = async () => {
    if (!formData.name || !formData.content) {
      alert('Please fill in all fields');
      return;
    }

    try {
      if (currentItem) {
        await updateTemplate(currentItem.id, formData);
      } else {
        await addTemplate(formData);
      }
      await loadData();
      setModalVisible(false);
      setFormData({});
      setCurrentItem(null);
    } catch (error) {
      alert('Error saving template: ' + error.message);
    }
  };

  const deleteTemplateItem = async (id) => {
    if (confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteTemplate(id);
        await loadData();
      } catch (error) {
        alert('Error deleting template: ' + error.message);
      }
    }
  };

  // Example functions
  const openExampleModal = (example = null) => {
    if (example) {
      setCurrentItem(example);
      setFormData({
        title: example.title,
        technologies: example.technologies || '',
        description: example.description,
        tags: example.tags,
        category: example.category,
        link: example.link || '',
        caseStudy: example.caseStudy || '',
        caseStudyContra: example.caseStudyContra || ''
      });
      setAiBuilderMode(false); // Editing existing examples uses manual mode
    } else {
      setCurrentItem(null);
      setFormData({ title: '', technologies: '', description: '', tags: '', category: '', link: '', caseStudy: '', caseStudyContra: '' });
      setAiBuilderMode(false); // Default to manual mode
      setAiBuilderData({
        description: '',
        websiteUrl: '',
        caseStudyUrl: '',
        additionalContext: ''
      });
    }
    setModalVisible(true);
  };

  const handleScanWebsite = async () => {
    if (!aiBuilderData.websiteUrl.trim()) {
      alert('Please enter a website URL first');
      return;
    }

    // Basic URL validation
    let urlToScan = aiBuilderData.websiteUrl.trim();
    if (!urlToScan.startsWith('http://') && !urlToScan.startsWith('https://')) {
      urlToScan = 'https://' + urlToScan;
      setAiBuilderData({ ...aiBuilderData, websiteUrl: urlToScan });
    }

    setScanningWebsite(true);
    try {
      const description = await scanWebsiteForDescription(urlToScan);
      setAiBuilderData({ ...aiBuilderData, description: description });
      alert('✅ Website scanned successfully! Description has been populated. You can edit it or add more context before generating.');
    } catch (error) {
      console.error('Error scanning website:', error);
      alert('Error scanning website: ' + error.message + '\n\nPlease try entering the description manually.');
    } finally {
      setScanningWebsite(false);
    }
  };

  const handleBuildExample = async () => {
    if (!aiBuilderData.description.trim()) {
      alert('Please provide a project description');
      return;
    }

    setBuildingExample(true);
    try {
      const generatedData = await buildProjectExample(aiBuilderData);
      
      // Populate form with generated data and switch back to manual mode for review
      setFormData(generatedData);
      setAiBuilderMode(false);
      setBuildingExample(false);
      
      alert('✅ AI generated your example! Review and edit the fields below, then click Save.');
    } catch (error) {
      alert('Error building example: ' + error.message);
      setBuildingExample(false);
    }
  };

  const saveExample = async () => {
    if (!formData.title || !formData.description) {
      alert('Please fill in title and description');
      return;
    }

    try {
      if (currentItem) {
        await updateProjectExample(currentItem.id, formData);
      } else {
        await addProjectExample(formData);
      }
      await loadData();
      setModalVisible(false);
      setFormData({});
      setCurrentItem(null);
    } catch (error) {
      alert('Error saving example: ' + error.message);
    }
  };

  const deleteExampleItem = async (id) => {
    if (confirm('Are you sure you want to delete this example?')) {
      try {
        await deleteProjectExample(id);
        await loadData();
      } catch (error) {
        alert('Error deleting example: ' + error.message);
      }
    }
  };

  // Contract Template functions
  const openContractModal = (contract = null) => {
    if (contract) {
      setCurrentItem(contract);
      setFormData({
        name: contract.name,
        content: contract.content,
        scope: contract.scope || 'personal'
      });
    } else {
      setCurrentItem(null);
      setFormData({ name: '', content: '', scope: 'personal' });
    }
    setModalVisible(true);
  };

  const saveContractTemplate = async () => {
    if (!formData.name || !formData.content) {
      alert('Please fill in all fields');
      return;
    }

    try {
      if (currentItem) {
        await updateContractTemplate(currentItem.id, formData);
      } else {
        await addContractTemplate(formData);
      }
      await loadData();
      setModalVisible(false);
      setFormData({});
      setCurrentItem(null);
    } catch (error) {
      alert('Error saving contract template: ' + error.message);
    }
  };

  const deleteContractItem = async (id) => {
    if (confirm('Are you sure you want to delete this contract template?')) {
      try {
        await deleteContractTemplate(id);
        await loadData();
      } catch (error) {
        alert('Error deleting contract template: ' + error.message);
      }
    }
  };

  const handleSeedContractTemplate = async () => {
    if (confirm('This will seed a default Design & Development Agreement template. Continue?')) {
      setLoading(true);
      try {
        const seeded = await seedContractTemplate(DEFAULT_CONTRACT_TEMPLATE);
        await loadData();
        if (seeded) {
          alert('✅ Successfully seeded contract template!');
        } else {
          alert('Template already exists');
        }
      } catch (error) {
        alert('Error seeding template: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };


  if (loading) {
    return (
      <View style={[styles.loadingContainer, { backgroundColor: colors.primary }]}>
        <ActivityIndicator size="large" color="#4a9eff" />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
      <LandingStyleHeader navigation={navigation} currentScreen="settings" />

      <View style={[styles.tabSelector, { backgroundColor: colors.secondary }]}>
        <TouchableOpacity 
          style={[styles.tabButton, { backgroundColor: colors.card }, activeTab === 'templates' && [styles.activeTabButton, { backgroundColor: colors.accent }]]}
          onPress={() => setActiveTab('templates')}
        >
          <Text style={[styles.tabButtonText, { color: colors.textSecondary }, activeTab === 'templates' && [styles.activeTabButtonText, { color: '#fff' }]]}>
            Templates
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.tabButton, { backgroundColor: colors.card }, activeTab === 'examples' && [styles.activeTabButton, { backgroundColor: colors.accent }]]}
          onPress={() => setActiveTab('examples')}
        >
          <Text style={[styles.tabButtonText, { color: colors.textSecondary }, activeTab === 'examples' && [styles.activeTabButtonText, { color: '#fff' }]]}>
            Project Examples
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.tabButton, { backgroundColor: colors.card }, activeTab === 'contracts' && [styles.activeTabButton, { backgroundColor: colors.accent }]]}
          onPress={() => setActiveTab('contracts')}
        >
          <Text style={[styles.tabButtonText, { color: colors.textSecondary }, activeTab === 'contracts' && [styles.activeTabButtonText, { color: '#fff' }]]}>
            Contracts
          </Text>
        </TouchableOpacity>
        <TouchableOpacity 
          style={[styles.tabButton, { backgroundColor: colors.card }, activeTab === 'preferences' && [styles.activeTabButton, { backgroundColor: colors.accent }]]}
          onPress={() => setActiveTab('preferences')}
        >
          <Text style={[styles.tabButtonText, { color: colors.textSecondary }, activeTab === 'preferences' && [styles.activeTabButtonText, { color: '#fff' }]]}>
            Preferences
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={[styles.content, { backgroundColor: colors.primary }]}>
        {activeTab === 'preferences' ? (
          <View>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Generator Preferences</Text>
            </View>

            <View style={[styles.preferenceCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
              <TouchableOpacity 
                style={styles.preferenceRow}
                onPress={handleToggleStrictAdherence}
              >
                <View style={{flex: 1}}>
                  <Text style={[styles.preferenceTitle, { color: colors.text }]}>Strict Adherence By Default</Text>
                  <Text style={[styles.preferenceDescription, { color: colors.textSecondary }]}>
                    When enabled, the "Strict Adherence" checkbox will be automatically checked when selecting templates in both the Proposal Wizard and Task Manager.
                  </Text>
                </View>
                <View style={[
                  styles.preferenceToggle,
                  strictAdherenceDefault && styles.preferenceToggleActive
                ]}>
                  <View style={[
                    styles.preferenceToggleKnob,
                    strictAdherenceDefault && styles.preferenceToggleKnobActive
                  ]} />
                </View>
              </TouchableOpacity>
            </View>

            <View style={[styles.preferenceInfoBox, { backgroundColor: colors.secondary, borderLeftColor: colors.accent }]}>
              <Text style={[styles.preferenceInfoTitle, { color: colors.accent }]}>About Strict Adherence</Text>
              <Text style={[styles.preferenceInfoText, { color: colors.textSecondary }]}>
                Strict Adherence Mode forces AI to follow your template structure exactly, only changing content in [brackets]. This ensures consistent formatting across all generated content.
              </Text>
            </View>
          </View>
        ) : activeTab === 'templates' ? (
          <>
            <View style={styles.templatesGrid}>
              {/* Proposal Templates Column */}
              <View style={styles.templateColumn}>
                <View style={styles.sectionHeader}>
                  <Text style={[styles.sectionTitle, { color: colors.text }]}>Proposal Templates</Text>
                  <TouchableOpacity onPress={() => openTemplateModal('proposal')} style={[styles.addButton, { backgroundColor: colors.accent }]}>
                    <Text style={[styles.addButtonText, { color: '#fff' }]}>+ Add</Text>
                  </TouchableOpacity>
                </View>

                {/* Organization Proposal Templates */}
                {templates.filter(t => (!t.templateType || t.templateType === 'proposal') && t.scope === 'organization').length > 0 && (
                  <>
                    <View style={[styles.scopeHeader, { borderBottomColor: colors.border }]}>
                      <Text style={[styles.scopeTitle, { color: colors.accent }]}>🏢 Organization Templates</Text>
                      <Text style={[styles.scopeSubtitle, { color: colors.textSecondary }]}>Shared with your team</Text>
                    </View>
                    {templates.filter(t => (!t.templateType || t.templateType === 'proposal') && t.scope === 'organization').map((template) => (
                      <View key={template.id} style={[styles.card, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                        <View style={styles.cardHeader}>
                          <Text style={[styles.cardTitle, { color: colors.text }]}>{template.name}</Text>
                          <View style={styles.templateTypeBadge}>
                            <Text style={styles.templateTypeBadgeText}>Proposal</Text>
                          </View>
                        </View>
                        <Text style={[styles.cardContent, { color: colors.textSecondary }]} numberOfLines={3}>
                          {template.content}
                        </Text>
                        {template.strictAdherence && (
                          <Text style={[styles.strictBadge, { color: colors.accent }]}>Strict Adherence Enabled</Text>
                        )}
                        <View style={styles.cardActions}>
                          <TouchableOpacity onPress={() => openTemplateModal(template)}>
                            <Text style={[styles.editButton, { color: colors.accent }]}>Edit</Text>
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => deleteTemplateItem(template.id)}>
                            <Text style={styles.deleteButton}>Delete</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))}
                  </>
                )}

                {/* Personal Proposal Templates */}
                {templates.filter(t => (!t.templateType || t.templateType === 'proposal') && t.scope === 'personal').length > 0 && (
                  <>
                    <View style={[styles.scopeHeader, { borderBottomColor: colors.border, marginTop: templates.filter(t => t.scope === 'organization').length > 0 ? 30 : 0 }]}>
                      <Text style={[styles.scopeTitle, { color: colors.accent }]}>👤 My Personal Templates</Text>
                      <Text style={[styles.scopeSubtitle, { color: colors.textSecondary }]}>Only visible to you</Text>
                    </View>
                    {templates.filter(t => (!t.templateType || t.templateType === 'proposal') && t.scope === 'personal').map((template) => (
                      <View key={template.id} style={[styles.card, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                        <View style={styles.cardHeader}>
                          <Text style={[styles.cardTitle, { color: colors.text }]}>{template.name}</Text>
                          <View style={styles.templateTypeBadge}>
                            <Text style={styles.templateTypeBadgeText}>Proposal</Text>
                          </View>
                        </View>
                        <Text style={[styles.cardContent, { color: colors.textSecondary }]} numberOfLines={3}>
                          {template.content}
                        </Text>
                        {template.strictAdherence && (
                          <Text style={[styles.strictBadge, { color: colors.accent }]}>Strict Adherence Enabled</Text>
                        )}
                        <View style={styles.cardActions}>
                          <TouchableOpacity onPress={() => openTemplateModal(template)}>
                            <Text style={[styles.editButton, { color: colors.accent }]}>Edit</Text>
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => deleteTemplateItem(template.id)}>
                            <Text style={styles.deleteButton}>Delete</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))}
                  </>
                )}

                {templates.filter(t => !t.templateType || t.templateType === 'proposal').length === 0 && (
                  <View style={styles.emptyState}>
                    <Text style={[styles.emptyText, { color: colors.text }]}>No proposal templates yet</Text>
                    <Text style={[styles.emptySubtext, { color: colors.textSecondary }]}>
                      Create a template to get started
                    </Text>
                  </View>
                )}
              </View>

              {/* Task Templates Column */}
              <View style={styles.templateColumn}>
                <View style={styles.sectionHeader}>
                  <Text style={[styles.sectionTitle, { color: colors.text }]}>Task Templates</Text>
                  <TouchableOpacity onPress={() => openTemplateModal('task')} style={[styles.addButton, { backgroundColor: colors.accent }]}>
                    <Text style={[styles.addButtonText, { color: '#fff' }]}>+ Add</Text>
                  </TouchableOpacity>
                </View>

                {/* Organization Task Templates */}
                {templates.filter(t => t.templateType === 'task' && t.scope === 'organization').length > 0 && (
                  <>
                    <View style={[styles.scopeHeader, { borderBottomColor: colors.border }]}>
                      <Text style={[styles.scopeTitle, { color: colors.accent }]}>🏢 Organization Templates</Text>
                      <Text style={[styles.scopeSubtitle, { color: colors.textSecondary }]}>Shared with your team</Text>
                    </View>
                    {templates.filter(t => t.templateType === 'task' && t.scope === 'organization').map((template) => (
                      <View key={template.id} style={[styles.card, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                        <View style={styles.cardHeader}>
                          <Text style={[styles.cardTitle, { color: colors.text }]}>{template.name}</Text>
                          <View style={[styles.templateTypeBadge, {backgroundColor: colors.accent}]}>
                            <Text style={styles.templateTypeBadgeText}>Task</Text>
                          </View>
                        </View>
                        <Text style={[styles.cardContent, { color: colors.textSecondary }]} numberOfLines={3}>
                          {template.content}
                        </Text>
                        {template.strictAdherence && (
                          <Text style={[styles.strictBadge, { color: colors.accent }]}>Strict Adherence Enabled</Text>
                        )}
                        <View style={styles.cardActions}>
                          <TouchableOpacity onPress={() => openTemplateModal(template)}>
                            <Text style={[styles.editButton, { color: colors.accent }]}>Edit</Text>
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => deleteTemplateItem(template.id)}>
                            <Text style={styles.deleteButton}>Delete</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))}
                  </>
                )}

                {/* Personal Task Templates */}
                {templates.filter(t => t.templateType === 'task' && t.scope === 'personal').length > 0 && (
                  <>
                    <View style={[styles.scopeHeader, { borderBottomColor: colors.border, marginTop: templates.filter(t => t.templateType === 'task' && t.scope === 'organization').length > 0 ? 30 : 0 }]}>
                      <Text style={[styles.scopeTitle, { color: colors.accent }]}>👤 My Personal Templates</Text>
                      <Text style={[styles.scopeSubtitle, { color: colors.textSecondary }]}>Only visible to you</Text>
                    </View>
                    {templates.filter(t => t.templateType === 'task' && t.scope === 'personal').map((template) => (
                      <View key={template.id} style={[styles.card, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                        <View style={styles.cardHeader}>
                          <Text style={[styles.cardTitle, { color: colors.text }]}>{template.name}</Text>
                          <View style={[styles.templateTypeBadge, {backgroundColor: colors.accent}]}>
                            <Text style={styles.templateTypeBadgeText}>Task</Text>
                          </View>
                        </View>
                        <Text style={[styles.cardContent, { color: colors.textSecondary }]} numberOfLines={3}>
                          {template.content}
                        </Text>
                        {template.strictAdherence && (
                          <Text style={[styles.strictBadge, { color: colors.accent }]}>Strict Adherence Enabled</Text>
                        )}
                        <View style={styles.cardActions}>
                          <TouchableOpacity onPress={() => openTemplateModal(template)}>
                            <Text style={[styles.editButton, { color: colors.accent }]}>Edit</Text>
                          </TouchableOpacity>
                          <TouchableOpacity onPress={() => deleteTemplateItem(template.id)}>
                            <Text style={styles.deleteButton}>Delete</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    ))}
                  </>
                )}

                {templates.filter(t => t.templateType === 'task').length === 0 && (
                  <View style={styles.emptyState}>
                    <Text style={[styles.emptyText, { color: colors.text }]}>No task templates yet</Text>
                    <Text style={[styles.emptySubtext, { color: colors.textSecondary }]}>
                      Create task templates for organizing your task lists
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </>
        ) : activeTab === 'contracts' ? (
          <>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Contract Templates</Text>
              <View style={{flexDirection: 'row', gap: 10}}>
                {/* Dev Only: Seed Template Button */}
                {process.env.NODE_ENV === 'development' && contractTemplates.length === 0 && (
                  <TouchableOpacity onPress={handleSeedContractTemplate} style={[styles.addButton, {backgroundColor: '#6a5aff'}]}>
                    <Text style={styles.addButtonText}>🌱 Seed Template</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={() => openContractModal()} style={[styles.addButton, { backgroundColor: colors.accent }]}>
                  <Text style={[styles.addButtonText, { color: '#fff' }]}>+ Add Contract</Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={[styles.strictBanner, { backgroundColor: '#fef3c7', borderColor: '#fbbf24', marginBottom: 30 }]}>
              <Text style={styles.strictBannerIcon}>🔒</Text>
              <View style={{ flex: 1 }}>
                <Text style={styles.strictBannerTitle}>Ultra-Strict Adherence Mode (Default)</Text>
                <Text style={styles.strictBannerText}>
                  Contract templates preserve all legal language. Only bracketed fields like [CLIENT_NAME], [PRICE], [DATE] are replaced during generation.
                </Text>
              </View>
            </View>

            {/* Organization Contract Templates */}
            {contractTemplates.filter(c => c.scope === 'organization').length > 0 && (
              <>
                <View style={[styles.scopeHeader, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.scopeTitle, { color: colors.accent }]}>🏢 Organization Contracts</Text>
                  <Text style={[styles.scopeSubtitle, { color: colors.textSecondary }]}>Shared with your team</Text>
                </View>
                {contractTemplates.filter(c => c.scope === 'organization').map((contract) => (
                  <View key={contract.id} style={[styles.card, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                    <View style={styles.cardHeader}>
                      <Text style={[styles.cardTitle, { color: colors.text }]}>{contract.name}</Text>
                    </View>
                    <Text style={[styles.cardContent, { color: colors.textSecondary }]} numberOfLines={3}>
                      {contract.content}
                    </Text>
                    <View style={styles.cardActions}>
                      <TouchableOpacity onPress={() => openContractModal(contract)}>
                        <Text style={[styles.editButton, { color: colors.accent }]}>Edit</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => deleteContractItem(contract.id)}>
                        <Text style={styles.deleteButton}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </>
            )}

            {/* Personal Contract Templates */}
            {contractTemplates.filter(c => c.scope === 'personal').length > 0 && (
              <>
                <View style={[styles.scopeHeader, { borderBottomColor: colors.border, marginTop: contractTemplates.filter(c => c.scope === 'organization').length > 0 ? 30 : 0 }]}>
                  <Text style={[styles.scopeTitle, { color: colors.accent }]}>👤 My Personal Contracts</Text>
                  <Text style={[styles.scopeSubtitle, { color: colors.textSecondary }]}>Only visible to you</Text>
                </View>
                {contractTemplates.filter(c => c.scope === 'personal').map((contract) => (
                  <View key={contract.id} style={[styles.card, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                    <View style={styles.cardHeader}>
                      <Text style={[styles.cardTitle, { color: colors.text }]}>{contract.name}</Text>
                    </View>
                    <Text style={[styles.cardContent, { color: colors.textSecondary }]} numberOfLines={3}>
                      {contract.content}
                    </Text>
                    <View style={styles.cardActions}>
                      <TouchableOpacity onPress={() => openContractModal(contract)}>
                        <Text style={[styles.editButton, { color: colors.accent }]}>Edit</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => deleteContractItem(contract.id)}>
                        <Text style={styles.deleteButton}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </>
            )}

            {contractTemplates.length === 0 && (
              <View style={styles.emptyState}>
                <Text style={[styles.emptyText, { color: colors.text }]}>No contract templates yet</Text>
                <Text style={[styles.emptySubtext, { color: colors.textSecondary }]}>
                  Create a contract template to get started. Paste your contract text with bracketed placeholders like [CLIENT_NAME], [PRICE], etc.
                </Text>
              </View>
            )}
          </>
        ) : (
          <>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>Project Examples</Text>
              <View style={{flexDirection: 'row', gap: 10}}>
                {examples.length === 0 ? (
                  <TouchableOpacity onPress={() => handleSeedExamples(false)} style={[styles.addButton, {backgroundColor: '#6a5aff'}]}>
                    <Text style={styles.addButtonText}>🌱 Seed 18 Examples</Text>
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity onPress={() => handleSeedExamples(true)} style={[styles.addButton, {backgroundColor: '#ff6a5a'}]}>
                    <Text style={styles.addButtonText}>🔄 Reseed Examples</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={() => openExampleModal()} style={styles.addButton}>
                  <Text style={styles.addButtonText}>+ Add Example</Text>
                </TouchableOpacity>
              </View>
            </View>

            {/* Organization Examples */}
            {examples.filter(e => e.scope === 'organization').length > 0 && (
              <>
                <View style={[styles.scopeHeader, { borderBottomColor: colors.border }]}>
                  <Text style={[styles.scopeTitle, { color: colors.accent }]}>🏢 Organization Examples</Text>
                  <Text style={[styles.scopeSubtitle, { color: colors.textSecondary }]}>Shared with your team</Text>
                </View>
                {examples.filter(e => e.scope === 'organization').map((example) => (
                  <View key={example.id} style={[styles.card, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                    <View style={{flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8}}>
                      <Text style={[styles.cardTitle, { color: colors.text }]}>{example.title}</Text>
                      {example.category && (
                        <Text style={[styles.categoryBadge, { backgroundColor: colors.accent }]}>{example.category}</Text>
                      )}
                    </View>
                    <Text style={[styles.cardDescription, { color: colors.textSecondary }]}>{example.description}</Text>
                    {example.technologies && (
                      <Text style={[styles.cardTech, { color: colors.accent }]}>Tech: {example.technologies}</Text>
                    )}
                    {example.tags && (
                      <Text style={[styles.cardTags, { color: colors.textSecondary }]}>Tags: {example.tags}</Text>
                    )}
                    {example.link && (
                      <Text style={styles.cardLink}>🔗 {example.link}</Text>
                    )}
                    <View style={styles.cardActions}>
                      <TouchableOpacity onPress={() => openExampleModal(example)}>
                        <Text style={[styles.editButton, { color: colors.accent }]}>Edit</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => deleteExampleItem(example.id)}>
                        <Text style={styles.deleteButton}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </>
            )}

            {/* Personal Examples */}
            {examples.filter(e => e.scope === 'personal').length > 0 && (
              <>
                <View style={[styles.scopeHeader, { borderBottomColor: colors.border, marginTop: examples.filter(e => e.scope === 'organization').length > 0 ? 30 : 0 }]}>
                  <Text style={[styles.scopeTitle, { color: colors.accent }]}>👤 My Personal Examples</Text>
                  <Text style={[styles.scopeSubtitle, { color: colors.textSecondary }]}>Only visible to you</Text>
                </View>
                {examples.filter(e => e.scope === 'personal').map((example) => (
                  <View key={example.id} style={[styles.card, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
                    <View style={{flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8}}>
                      <Text style={[styles.cardTitle, { color: colors.text }]}>{example.title}</Text>
                      {example.category && (
                        <Text style={[styles.categoryBadge, { backgroundColor: colors.accent }]}>{example.category}</Text>
                      )}
                    </View>
                    <Text style={[styles.cardDescription, { color: colors.textSecondary }]}>{example.description}</Text>
                    {example.technologies && (
                      <Text style={[styles.cardTech, { color: colors.accent }]}>Tech: {example.technologies}</Text>
                    )}
                    {example.tags && (
                      <Text style={[styles.cardTags, { color: colors.textSecondary }]}>Tags: {example.tags}</Text>
                    )}
                    {example.link && (
                      <Text style={styles.cardLink}>🔗 {example.link}</Text>
                    )}
                    <View style={styles.cardActions}>
                      <TouchableOpacity onPress={() => openExampleModal(example)}>
                        <Text style={[styles.editButton, { color: colors.accent }]}>Edit</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={() => deleteExampleItem(example.id)}>
                        <Text style={styles.deleteButton}>Delete</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </>
            )}

            {examples.length === 0 && (
              <View style={styles.emptyState}>
                <Text style={[styles.emptyText, { color: colors.text }]}>No examples yet</Text>
                <Text style={[styles.emptySubtext, { color: colors.textSecondary }]}>
                  Add portfolio examples to showcase your work in proposals
                </Text>
              </View>
            )}
          </>
        )}
      </ScrollView>

      {/* Modal for adding/editing */}
      <Modal
        visible={modalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {currentItem ? 'Edit' : 'Add'} {activeTab === 'templates' ? 'Template' : activeTab === 'contracts' ? 'Contract' : 'Example'}
            </Text>

            {/* AI Builder Toggle - Only for new examples */}
            {activeTab === 'examples' && !currentItem && (
              <TouchableOpacity 
                style={styles.aiToggleContainer}
                onPress={() => {
                  setAiBuilderMode(!aiBuilderMode);
                  // Clear both forms when switching
                  if (!aiBuilderMode) {
                    setAiBuilderData({
                      description: '',
                      websiteUrl: '',
                      caseStudyUrl: '',
                      additionalContext: ''
                    });
                  } else {
                    setFormData({ title: '', technologies: '', description: '', tags: '', category: '', link: '', caseStudy: '', caseStudyContra: '' });
                  }
                }}
              >
                <View style={[styles.aiCheckbox, aiBuilderMode && styles.aiCheckboxChecked]}>
                  {aiBuilderMode && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <View style={{flex: 1}}>
                  <Text style={styles.aiToggleLabel}>🤖 Generate with AI</Text>
                  <Text style={styles.aiToggleDesc}>
                    Let AI build the example data from your description
                  </Text>
                </View>
              </TouchableOpacity>
            )}

            {activeTab === 'templates' ? (
              <>
                <Text style={styles.label}>Template Name:</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g., Standard Proposal, Technical Bid, etc."
                  placeholderTextColor="#666"
                  value={formData.name || ''}
                  onChangeText={(text) => setFormData({ ...formData, name: text })}
                />

                <Text style={styles.label}>Template Type:</Text>
                <View style={styles.templateTypeContainer}>
                  <TouchableOpacity
                    style={[
                      styles.templateTypeButton,
                      formData.templateType === 'proposal' && styles.templateTypeButtonActive
                    ]}
                    onPress={() => setFormData({ ...formData, templateType: 'proposal' })}
                  >
                    <Text style={[
                      styles.templateTypeButtonText,
                      formData.templateType === 'proposal' && styles.templateTypeButtonTextActive
                    ]}>
                      Proposal Template
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.templateTypeButton,
                      formData.templateType === 'task' && styles.templateTypeButtonActive
                    ]}
                    onPress={() => setFormData({ ...formData, templateType: 'task' })}
                  >
                    <Text style={[
                      styles.templateTypeButtonText,
                      formData.templateType === 'task' && styles.templateTypeButtonTextActive
                    ]}>
                      Task Template
                    </Text>
                  </TouchableOpacity>
                </View>
                <Text style={styles.templateTypeDesc}>
                  {formData.templateType === 'proposal' 
                    ? 'Use for generating client proposals and bids'
                    : 'Use for organizing and managing task lists'}
                </Text>

                <Text style={styles.label}>Template Content:</Text>
                <TextInput
                  style={[styles.textArea, {minHeight: 300}]}
                  placeholder={formData.templateType === 'proposal' 
                    ? "Enter your proposal template here..." 
                    : "Enter your task list template here..."}
                  placeholderTextColor="#666"
                  value={formData.content || ''}
                  onChangeText={(text) => setFormData({ ...formData, content: text })}
                  multiline
                  numberOfLines={15}
                />

                <TouchableOpacity
                  style={styles.checkboxContainer}
                  onPress={() => setFormData({ ...formData, strictAdherence: !formData.strictAdherence })}
                >
                  <View style={[styles.checkbox, formData.strictAdherence && styles.checkboxChecked]}>
                    {formData.strictAdherence && <Text style={styles.checkboxCheck}>✓</Text>}
                  </View>
                  <Text style={styles.checkboxLabel}>Enable Strict Adherence by Default</Text>
                </TouchableOpacity>
                <Text style={styles.checkboxHint}>
                  When enabled, AI will follow template structure exactly
                </Text>
              </>
            ) : activeTab === 'contracts' ? (
              <>
                <Text style={styles.label}>Contract Name:</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g., Design & Development Agreement"
                  placeholderTextColor="#666"
                  value={formData.name || ''}
                  onChangeText={(text) => setFormData({ ...formData, name: text })}
                />

                <Text style={styles.label}>Scope:</Text>
                <View style={styles.templateTypeContainer}>
                  <TouchableOpacity
                    style={[
                      styles.templateTypeButton,
                      formData.scope === 'personal' && styles.templateTypeButtonActive
                    ]}
                    onPress={() => setFormData({ ...formData, scope: 'personal' })}
                  >
                    <Text style={[
                      styles.templateTypeButtonText,
                      formData.scope === 'personal' && styles.templateTypeButtonTextActive
                    ]}>
                      👤 Personal
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.templateTypeButton,
                      formData.scope === 'organization' && styles.templateTypeButtonActive
                    ]}
                    onPress={() => setFormData({ ...formData, scope: 'organization' })}
                  >
                    <Text style={[
                      styles.templateTypeButtonText,
                      formData.scope === 'organization' && styles.templateTypeButtonTextActive
                    ]}>
                      🏢 Organization
                    </Text>
                  </TouchableOpacity>
                </View>

                <Text style={styles.label}>Contract Template:</Text>
                <Text style={styles.checkboxHint}>
                  Paste your contract text with bracketed placeholders like [CLIENT_NAME], [PRICE], [PROJECT_TYPE], [START_DATE], etc. Only these placeholders will be replaced.
                </Text>
                <TextInput
                  style={[styles.textArea, { minHeight: 400 }]}
                  placeholder="Paste your contract template here...

Example placeholders:
[CLIENT_NAME] - Client's full name
[CLIENT_COMPANY] - Client's company
[PROJECT_TYPE] - Type of project
[PROJECT_DETAILS] - Project description
[TOTAL_PRICE] - Total project price
[PAYMENT_TERMS] - Payment terms
[START_DATE] - Project start date
[COMPLETION_DATE] - Completion date
[YOUR_NAME] - Your name
[YOUR_COMPANY] - Your company
[YOUR_TITLE] - Your title"
                  placeholderTextColor="#666"
                  value={formData.content || ''}
                  onChangeText={(text) => setFormData({ ...formData, content: text })}
                  multiline
                />
              </>
            ) : aiBuilderMode ? (
              <>
                <Text style={styles.aiBuilderDesc}>
                  Provide details about your project. AI will generate title, technologies, description, category, and tags.
                </Text>

                {/* Website URL Scanner */}
                <View style={styles.websiteScannerContainer}>
                  <Text style={styles.websiteScannerTitle}>🌐 Scan Website to Auto-Fill</Text>
                  <Text style={styles.websiteScannerDesc}>
                    Enter a project website URL and click "Scan" to automatically generate a detailed description
                  </Text>
                  
                  <View style={styles.urlInputRow}>
                    <TextInput
                      style={styles.urlInput}
                      placeholder="https://example.com"
                      placeholderTextColor="#666"
                      value={aiBuilderData.websiteUrl}
                      onChangeText={(text) => setAiBuilderData({ ...aiBuilderData, websiteUrl: text })}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    <TouchableOpacity 
                      style={[styles.scanButton, scanningWebsite && styles.scanButtonDisabled]}
                      onPress={handleScanWebsite}
                      disabled={scanningWebsite || !aiBuilderData.websiteUrl.trim()}
                    >
                      {scanningWebsite ? (
                        <ActivityIndicator color="#fff" size="small" />
                      ) : (
                        <Text style={styles.scanButtonText}>🔍 Scan</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                  
                  {scanningWebsite && (
                    <Text style={styles.scanningText}>
                      Analyzing website...
                    </Text>
                  )}
                </View>

                <Text style={styles.label}>Project Description: *</Text>
                <TextInput
                  style={[styles.textArea, {minHeight: 150}]}
                  placeholder="Example: Built a React Native dating app with video profiles, real-time Firebase chat, Stripe subscriptions, and swipe matching. Achieved 10k users in first month..."
                  placeholderTextColor="#666"
                  value={aiBuilderData.description}
                  onChangeText={(text) => setAiBuilderData({ ...aiBuilderData, description: text })}
                  multiline
                  numberOfLines={8}
                />

                <Text style={styles.label}>Case Study URL (optional):</Text>
                <TextInput
                  style={styles.input}
                  placeholder="https://upwork.com/portfolio/..."
                  placeholderTextColor="#666"
                  value={aiBuilderData.caseStudyUrl}
                  onChangeText={(text) => setAiBuilderData({ ...aiBuilderData, caseStudyUrl: text })}
                />

                <Text style={styles.label}>Additional Context (optional):</Text>
                <TextInput
                  style={styles.textArea}
                  placeholder="Team size, your specific role, challenges overcome, results achieved, awards, etc."
                  placeholderTextColor="#666"
                  value={aiBuilderData.additionalContext}
                  onChangeText={(text) => setAiBuilderData({ ...aiBuilderData, additionalContext: text })}
                  multiline
                  numberOfLines={4}
                />
              </>
            ) : (
              <>
                <Text style={styles.label}>Project Title:</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g., E-commerce Website Redesign"
                  placeholderTextColor="#666"
                  value={formData.title || ''}
                  onChangeText={(text) => setFormData({ ...formData, title: text })}
                />

                <Text style={styles.label}>Technologies:</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g., Expo + React, Firebase, Stripe"
                  placeholderTextColor="#666"
                  value={formData.technologies || ''}
                  onChangeText={(text) => setFormData({ ...formData, technologies: text })}
                />

                <Text style={styles.label}>Category:</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g., Mobile Applications, eCommerce, etc."
                  placeholderTextColor="#666"
                  value={formData.category || ''}
                  onChangeText={(text) => setFormData({ ...formData, category: text })}
                />

                <Text style={styles.label}>Description:</Text>
                <TextInput
                  style={styles.textArea}
                  placeholder="Describe the project..."
                  placeholderTextColor="#666"
                  value={formData.description || ''}
                  onChangeText={(text) => setFormData({ ...formData, description: text })}
                  multiline
                  numberOfLines={6}
                />

                <Text style={styles.label}>Tags (comma separated):</Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g., React, Firebase, Stripe, mobile"
                  placeholderTextColor="#666"
                  value={formData.tags || ''}
                  onChangeText={(text) => setFormData({ ...formData, tags: text })}
                />

                <Text style={styles.label}>Website Link (optional):</Text>
                <TextInput
                  style={styles.input}
                  placeholder="https://..."
                  placeholderTextColor="#666"
                  value={formData.link || ''}
                  onChangeText={(text) => setFormData({ ...formData, link: text })}
                />

                <Text style={styles.label}>Case Study Link - Generic/Upwork (optional):</Text>
                <TextInput
                  style={styles.input}
                  placeholder="https://upwork.com/freelancers/..."
                  placeholderTextColor="#666"
                  value={formData.caseStudy || ''}
                  onChangeText={(text) => setFormData({ ...formData, caseStudy: text })}
                />

                <Text style={styles.label}>Case Study Link - Contra (optional):</Text>
                <Text style={styles.helpText}>
                  💼 Used when copying for Contra (replaces generic link)
                </Text>
                <TextInput
                  style={styles.input}
                  placeholder="https://contra.com/..."
                  placeholderTextColor="#666"
                  value={formData.caseStudyContra || ''}
                  onChangeText={(text) => setFormData({ ...formData, caseStudyContra: text })}
                />
              </>
            )}

            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={[styles.modalButton, styles.cancelButton]} 
                onPress={() => {
                  setModalVisible(false);
                  setFormData({});
                  setCurrentItem(null);
                  setAiBuilderMode(false);
                  setAiBuilderData({
                    description: '',
                    websiteUrl: '',
                    caseStudyUrl: '',
                    additionalContext: ''
                  });
                }}
              >
                <Text style={styles.modalButtonText}>Cancel</Text>
              </TouchableOpacity>
              {aiBuilderMode ? (
                <TouchableOpacity 
                  style={[styles.modalButton, styles.aiButton, buildingExample && styles.disabledButton]} 
                  onPress={handleBuildExample}
                  disabled={buildingExample}
                >
                  {buildingExample ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <Text style={styles.modalButtonText}>🤖 Build with AI</Text>
                  )}
                </TouchableOpacity>
              ) : (
                <TouchableOpacity 
                  style={[styles.modalButton, styles.saveModalButton]} 
                  onPress={activeTab === 'templates' ? saveTemplate : activeTab === 'contracts' ? saveContractTemplate : saveExample}
                >
                  <Text style={styles.modalButtonText}>Save</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        </View>
      </Modal>

    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    paddingTop: 100,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabSelector: {
    flexDirection: 'row',
    backgroundColor: '#f8fafc',
    padding: 10,
    gap: 10,
  },
  tabButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    alignItems: 'center',
  },
  activeTabButton: {
    backgroundColor: '#4a9eff',
  },
  tabButtonText: {
    color: '#1e293b',
    fontWeight: '600',
  },
  activeTabButtonText: {
    color: '#1e293b',
  },
  content: {
    padding: 20,
    width: '100%',
  },
  templatesGrid: {
    flexDirection: 'row',
    gap: 30,
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  templateColumn: {
    flex: 1,
    minWidth: 0,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 10,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  addButton: {
    backgroundColor: '#4a9eff',
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 6,
  },
  addButtonText: {
    color: '#1e293b',
    fontSize: 14,
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    marginTop: 60,
  },
  emptyText: {
    color: '#1e293b',
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 10,
  },
  emptySubtext: {
    color: '#1e293b',
    textAlign: 'center',
    paddingHorizontal: 40,
  },
  card: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 15,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  cardTitle: {
    color: '#1e293b',
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  cardContent: {
    color: '#1e293b',
    marginBottom: 10,
    lineHeight: 20,
  },
  cardDescription: {
    color: '#1e293b',
    marginBottom: 8,
    lineHeight: 20,
  },
  cardTags: {
    color: '#4a9eff',
    fontSize: 12,
    marginBottom: 8,
  },
  cardLink: {
    color: '#4a9eff',
    fontSize: 12,
    marginBottom: 8,
  },
  categoryBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  cardActions: {
    flexDirection: 'row',
    gap: 15,
    marginTop: 5,
  },
  editButton: {
    color: '#4a9eff',
    fontSize: 14,
  },
  deleteButton: {
    color: '#ff4444',
    fontSize: 14,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 25,
    width: '100%',
    maxWidth: 600,
    maxHeight: '90%',
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: 20,
  },
  label: {
    color: '#1e293b',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  helpText: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 8,
    fontStyle: 'italic',
  },
  input: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 15,
    color: '#1e293b',
    fontSize: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  textArea: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 12,
    marginBottom: 15,
    color: '#1e293b',
    fontSize: 14,
    minHeight: 120,
    textAlignVertical: 'top',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 10,
  },
  modalButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: '#333',
  },
  saveModalButton: {
    backgroundColor: '#4a9eff',
  },
  aiButton: {
    backgroundColor: '#6a5aff',
  },
  disabledButton: {
    opacity: 0.5,
  },
  aiToggleContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 15,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    marginBottom: 20,
    borderWidth: 2,
    borderColor: '#6a5aff',
    gap: 12,
  },
  aiCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#666',
    marginTop: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  aiCheckboxChecked: {
    borderColor: '#6a5aff',
    backgroundColor: '#6a5aff',
  },
  checkmark: {
    color: '#1e293b',
    fontSize: 16,
    fontWeight: 'bold',
  },
  aiToggleLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6a5aff',
    marginBottom: 4,
  },
  aiToggleDesc: {
    fontSize: 13,
    color: '#1e293b',
    lineHeight: 18,
  },
  aiBuilderDesc: {
    fontSize: 14,
    color: '#1e293b',
    marginBottom: 20,
    lineHeight: 20,
    padding: 12,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#6a5aff',
  },
  modalButtonText: {
    color: '#1e293b',
    fontSize: 16,
    fontWeight: '600',
  },
  navButton: {
    padding: 8,
    marginRight: 5,
  },
  navButtonText: {
    fontSize: 20,
    color: '#1e293b',
  },
  websiteScannerContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
    borderWidth: 2,
    borderColor: '#6a5aff',
    borderStyle: 'dashed',
  },
  websiteScannerTitle: {
    fontSize: 15,
    fontWeight: 'bold',
    color: '#6a5aff',
    marginBottom: 5,
  },
  websiteScannerDesc: {
    fontSize: 12,
    color: '#1e293b',
    marginBottom: 12,
    lineHeight: 16,
  },
  urlInputRow: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
  },
  urlInput: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 12,
    color: '#1e293b',
    fontSize: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  scanButton: {
    backgroundColor: '#6a5aff',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
    height: 46,
  },
  scanButtonDisabled: {
    backgroundColor: '#666',
    opacity: 0.6,
  },
  scanButtonText: {
    color: '#1e293b',
    fontSize: 13,
    fontWeight: '600',
  },
  scanningText: {
    color: '#6a5aff',
    fontSize: 12,
    marginTop: 10,
    fontStyle: 'italic',
  },
  templateTypeContainer: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 10,
  },
  templateTypeButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#e2e8f0',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  templateTypeButtonActive: {
    borderColor: '#4a9eff',
    backgroundColor: '#1a2a3a',
  },
  templateTypeButtonText: {
    color: '#64748b',
    fontSize: 14,
    fontWeight: '600',
  },
  templateTypeButtonTextActive: {
    color: '#4a9eff',
  },
  templateTypeDesc: {
    fontSize: 12,
    color: '#1e293b',
    marginBottom: 15,
    lineHeight: 18,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 15,
    marginBottom: 8,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#4a9eff',
    marginRight: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#4a9eff',
  },
  checkboxCheck: {
    color: '#1e293b',
    fontSize: 16,
    fontWeight: 'bold',
  },
  checkboxLabel: {
    fontSize: 14,
    color: '#1e293b',
  },
  checkboxHint: {
    fontSize: 12,
    color: '#64748b',
    marginLeft: 34,
    marginBottom: 15,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  templateTypeBadge: {
    backgroundColor: '#4a9eff',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
  },
  templateTypeBadgeText: {
    color: '#1e293b',
    fontSize: 11,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  strictBadge: {
    fontSize: 11,
    color: '#6a5aff',
    fontStyle: 'italic',
    marginTop: 5,
    marginBottom: 5,
  },
  // Preferences Tab
  preferenceCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  preferenceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 15,
  },
  preferenceTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: 6,
  },
  preferenceDescription: {
    fontSize: 13,
    color: '#1e293b',
    lineHeight: 20,
  },
  preferenceToggle: {
    width: 50,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#333',
    padding: 2,
    justifyContent: 'center',
  },
  preferenceToggleActive: {
    backgroundColor: '#4a9eff',
  },
  preferenceToggleKnob: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  preferenceToggleKnobActive: {
    alignSelf: 'flex-end',
  },
  preferenceInfoBox: {
    backgroundColor: '#1a2a3a',
    borderRadius: 8,
    padding: 15,
    borderLeftWidth: 3,
    borderLeftColor: '#4a9eff',
  },
  preferenceInfoTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4a9eff',
    marginBottom: 8,
  },
  preferenceInfoText: {
    fontSize: 13,
    color: '#1e293b',
    lineHeight: 20,
  },
  scopeHeader: {
    paddingBottom: 12,
    marginBottom: 20,
    borderBottomWidth: 1,
  },
  scopeTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  scopeSubtitle: {
    fontSize: 13,
    fontStyle: 'italic',
  },
  strictBanner: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'flex-start',
  },
  strictBannerIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  strictBannerTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
  },
  strictBannerText: {
    fontSize: 13,
    lineHeight: 18,
  },
});
